import React from 'react';
import {
  BellRing, Clock, MessageCircle, AlertCircle, CalendarClock, Send, Repeat, CheckCheck, FileText,
} from '../components/Icons';

const PAIN_POINTS = [
  {
    icon: BellRing,
    title: 'Forgot to send this month\'s invoices?',
    description: 'Month end gets busy and invoices go out late, or not at all. A late invoice means a late payment.',
  },
  {
    icon: Clock,
    title: 'Are past invoices still pending?',
    description: 'Old invoices sit unpaid because nobody followed up. The money is yours, it just is not in your account yet.',
  },
  {
    icon: MessageCircle,
    title: 'Sending invoices one by one?',
    description: 'Download the PDF, find the client\'s number, type the same message. For every client, every month.',
  },
  {
    icon: AlertCircle,
    title: '"We never received the invoice"',
    description: 'Without a record of what was sent and when, it is your word against theirs.',
  },
];

const FEATURES = [
  {
    icon: CalendarClock,
    title: 'A monthly schedule you set once',
    description: 'Pick the day, for example the 1st of every month, and invoices go out on time without anyone remembering.',
  },
  {
    icon: Send,
    title: 'The invoice PDF, straight to WhatsApp',
    description: 'Each client gets their own invoice as a PDF on WhatsApp, with the FBR invoice number and QR code on it.',
  },
  {
    icon: Repeat,
    title: 'Automatic reminders for unpaid invoices',
    description: 'Pending invoices get a polite reminder after the number of days you choose, until you mark them paid.',
  },
  {
    icon: CheckCheck,
    title: 'A record of every message',
    description: 'See which invoices were sent to whom and when, so "never received it" has an answer.',
  },
];

const STEPS = [
  { title: '1. Choose clients and a date', description: 'Select the clients you bill every month and the day invoices should go out.' },
  { title: '2. Invoices go out on WhatsApp', description: 'On that day each client receives their invoice PDF with a short message.' },
  { title: '3. Reminders follow until paid', description: 'Unpaid invoices are followed up automatically, so you stop chasing payments by hand.' },
];

const Bubble = ({ children, time }) => (
  <div style={{ maxWidth: '85%' }} className="rounded-lg rounded-tl-none bg-white px-3 py-2 text-sm text-gray-800 shadow-sm">
    {children}
    <div className="mt-1 flex items-center justify-end gap-1 text-xs text-gray-400">
      {time} <CheckCheck className="h-3.5 w-3.5 text-sky-500" />
    </div>
  </div>
);

// The automatic WhatsApp invoices tab of the Pro Features page.
const WhatsAppInvoicesPro = ({ whatsappLink, businessName }) => (
  <>
    <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="p-6 flex flex-col justify-center">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
            <MessageCircle className="h-3.5 w-3.5" /> Automatic WhatsApp invoices
          </span>
          <h2 className="mt-3 text-2xl font-bold text-gray-900">
            Send every client their monthly invoice on WhatsApp, automatically
          </h2>
          <p className="mt-2 text-gray-600">
            Set it up once. On the date you choose, each client receives their invoice on WhatsApp,
            and unpaid invoices get a reminder until they are paid.
          </p>
          <a
            href={whatsappLink('Automatic WhatsApp Invoices Pro feature')}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex w-fit items-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-medium text-white hover:bg-emerald-700 transition-colors"
          >
            <MessageCircle className="h-5 w-5" /> Request access on WhatsApp
          </a>
        </div>

        {/* An illustration of what a client sees, not a live conversation. */}
        <div className="p-5" style={{ backgroundColor: '#efeae2' }}>
          <div className="mx-auto max-w-sm overflow-hidden rounded-xl shadow-lg">
            <div className="flex items-center gap-3 px-4 py-3 text-white" style={{ backgroundColor: '#075e54' }}>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-sm font-semibold">
                {(businessName || 'Y').charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-sm font-semibold">{businessName || 'Your Business'}</div>
                <div className="text-xs text-white/70">Business account</div>
              </div>
            </div>
            <div className="space-y-3 px-3 py-4" style={{ backgroundColor: '#efeae2' }}>
              <div className="mx-auto w-fit rounded-md bg-white/80 px-2 py-0.5 text-xs text-gray-500">1 October</div>
              <Bubble time="9:00 AM">
                <p>Assalam o Alaikum Ahmed Traders, your invoice for September is attached.</p>
                <p className="mt-1">Amount due: <strong>Rs. 45,000</strong>, payable by 10 October.</p>
                <div className="mt-2 flex items-center gap-2 rounded-md bg-gray-100 px-2 py-1.5">
                  <FileText className="h-5 w-5 text-red-500" />
                  <div className="text-xs">
                    <div className="font-medium text-gray-700">Invoice 1042.pdf</div>
                    <div className="text-gray-400">PDF, with FBR invoice number</div>
                  </div>
                </div>
              </Bubble>
              <div className="mx-auto w-fit rounded-md bg-white/80 px-2 py-0.5 text-xs text-gray-500">12 October</div>
              <Bubble time="10:00 AM">
                <p>A gentle reminder that invoice 1042 for Rs. 45,000 is still pending. Please ignore this if you have already paid.</p>
              </Bubble>
            </div>
          </div>
        </div>
      </div>
    </div>

    <h3 className="mb-3 text-lg font-semibold text-gray-900">Sound familiar?</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
      {PAIN_POINTS.map((p) => (
        <div key={p.title} className="bg-white rounded-lg shadow p-5 flex gap-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500">
            <p.icon className="h-5 w-5" />
          </div>
          <div>
            <div className="font-semibold text-gray-900">{p.title}</div>
            <div className="mt-1 text-sm text-gray-500">{p.description}</div>
          </div>
        </div>
      ))}
    </div>

    <h3 className="mb-3 text-lg font-semibold text-gray-900">Let invoicing run on its own</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
      {FEATURES.map((f) => (
        <div key={f.title} className="bg-white rounded-lg shadow p-5 flex gap-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
            <f.icon className="h-5 w-5" />
          </div>
          <div>
            <div className="font-semibold text-gray-900">{f.title}</div>
            <div className="mt-1 text-sm text-gray-500">{f.description}</div>
          </div>
        </div>
      ))}
    </div>

    <h3 className="mb-3 text-lg font-semibold text-gray-900">How it works</h3>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {STEPS.map((s) => (
        <div key={s.title} className="bg-white rounded-lg shadow p-5">
          <div className="mb-1 font-semibold text-gray-900">{s.title}</div>
          <div className="text-sm text-gray-500">{s.description}</div>
        </div>
      ))}
    </div>

    <div className="mt-6 bg-white rounded-lg shadow p-6 flex flex-wrap items-center justify-between gap-4">
      <div>
        <div className="text-lg font-semibold text-gray-900">Stop chasing invoices by hand</div>
        <div className="mt-1 text-sm text-gray-500">Tell us how many clients you bill each month and we will set it up for you.</div>
      </div>
      <a
        href={whatsappLink('Automatic WhatsApp Invoices Pro feature')}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 text-base font-medium text-white hover:bg-emerald-700 transition-colors"
      >
        <MessageCircle className="h-5 w-5" /> Request access on WhatsApp
      </a>
    </div>
  </>
);

export default WhatsAppInvoicesPro;
